import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'

export function CatalogLayoutsTab() {
  return (
    <div className="mt-10 space-y-6 px-6">
      <div className="space-y-2">
        <h2 className="text-foreground" style={typographyStyle(designTokens.typography.heading.h2)}>
          Layouts
        </h2>
        <p
          className="max-w-3xl text-muted-foreground"
          style={typographyStyle(designTokens.typography.body.sm)}
        >
          Common layout patterns composed from shared primitives and widgets.
        </p>
      </div>

      <div className="space-y-8">
        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Dashboard layout</h3>
            <p className="text-sm text-muted-foreground">
              Header, KPI cards, and a two-column content grid.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Desktop</CardTitle>
                <p className="text-xs text-muted-foreground">12-column grid with cards.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <span>Header</span>
                  <Button size="sm" variant="secondary">
                    Action
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {['Revenue', 'Users', 'ARPU'].map((label) => (
                    <div
                      key={label}
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs"
                    >
                      <p className="text-muted-foreground">{label}</p>
                      <p className="text-sm font-semibold">123</p>
                    </div>
                  ))}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="h-20 rounded-md border border-dashed border-border bg-muted/30" />
                  <div className="h-20 rounded-md border border-dashed border-border bg-muted/30" />
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<Header />\n<MetricsGrid />\n<ContentGrid />`}</code>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Mobile</CardTitle>
                <p className="text-xs text-muted-foreground">Stacked cards with CTA.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  Header
                </div>
                <div className="grid gap-2">
                  {['Revenue', 'Users', 'ARPU'].map((label) => (
                    <div
                      key={label}
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs"
                    >
                      <p className="text-muted-foreground">{label}</p>
                      <p className="text-sm font-semibold">123</p>
                    </div>
                  ))}
                </div>
                <Button size="sm">Primary action</Button>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<Header />\n<MetricCards />\n<Action />`}</code>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Form layout</h3>
            <p className="text-sm text-muted-foreground">
              Two-column form on desktop, single column on mobile.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Desktop</CardTitle>
                <p className="text-xs text-muted-foreground">Split inputs with sidebar.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-3 md:grid-cols-[2fr,1fr]">
                  <div className="space-y-2">
                    <Input placeholder="Company name" />
                    <Input placeholder="Email" />
                    <Textarea placeholder="Notes" rows={3} />
                    <div className="flex gap-2">
                      <Button size="sm">Save</Button>
                      <Button size="sm" variant="secondary">
                        Cancel
                      </Button>
                    </div>
                  </div>
                  <div className="h-full rounded-md border border-dashed border-border bg-muted/30" />
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<FormGrid>\n  <Fields />\n  <Sidebar />\n</FormGrid>`}</code>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Mobile</CardTitle>
                <p className="text-xs text-muted-foreground">Stacked fields with footer.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input placeholder="Company name" />
                <Input placeholder="Email" />
                <Textarea placeholder="Notes" rows={3} />
                <Button size="sm">Save</Button>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<FormStack />\n<StickyCTA />`}</code>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Card grid layout</h3>
            <p className="text-sm text-muted-foreground">
              Dense cards with responsive grid breakpoints.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Desktop</CardTitle>
                <p className="text-xs text-muted-foreground">3-column grid.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 md:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={`desktop-${index}`}
                      className="h-16 rounded-md border border-border bg-muted/30"
                    />
                  ))}
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<Grid columns={3} />`}</code>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Mobile</CardTitle>
                <p className="text-xs text-muted-foreground">Single-column list.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`mobile-${index}`}
                      className="h-16 rounded-md border border-border bg-muted/30"
                    />
                  ))}
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                  <code>{`<Grid columns={1} />`}</code>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  )
}
